import { useEffect, useState } from "react";
import { MapPin, Calendar, Ticket } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function Events() {
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", role: "investor" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/events").then((r) => setEvents(r.data)); }, []);

  const register = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email.includes("@")) { toast.error("Please fill in your name and a valid email."); return; }
    setSubmitting(true);
    try {
      await api.post(`/events/${selected.id}/register`, form);
      toast.success(`You're registered for "${selected.title}"!`);
      setEvents((evs) => evs.map((ev) => (ev.id === selected.id ? { ...ev, seats_left: Math.max(0, ev.seats_left - 1) } : ev)));
      setSelected(null);
      setForm({ name: "", email: "", role: "investor" });
    } catch (err) {
      toast.error("Registration failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="relative pt-28 pb-24 max-w-7xl mx-auto px-4 sm:px-6">
      <div className="noise-overlay" />
      <div className="text-left mb-12">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
          Events & <span className="gold-gradient-text">Shark Tank</span>
        </h1>
        <p className="mt-3 text-zinc-500 text-sm md:text-base max-w-lg">Founders pitch live to a panel of investors — Shark Tank style. Register to attend or pitch.</p>
      </div>
      <div className="space-y-8" data-testid="events-list">
        {events.map((ev) => (
          <div key={ev.id} data-testid={`event-card-${ev.id}`} className="rounded-2xl overflow-hidden bg-[#0d0d0d] border border-white/5 grid md:grid-cols-[380px_1fr] gold-glow-hover">
            <div className="relative h-56 md:h-auto">
              <img src={ev.image} alt={ev.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0d0d0d]/60 hidden md:block" />
            </div>
            <div className="p-7 text-left flex flex-col">
              <h2 className="text-2xl font-semibold text-white">{ev.title}</h2>
              <div className="mt-3 flex flex-wrap gap-4 text-xs text-zinc-400">
                <span className="flex items-center gap-1.5"><Calendar size={13} className="text-[#F5B301]" /> {ev.date}</span>
                <span className="flex items-center gap-1.5"><MapPin size={13} className="text-[#F5B301]" /> {ev.location}</span>
                <span className="flex items-center gap-1.5"><Ticket size={13} className="text-[#F5B301]" /> {ev.seats_left} seats left</span>
              </div>
              <p className="mt-4 text-sm text-zinc-400 leading-relaxed">{ev.description}</p>
              <div className="mt-4">
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Investor Panel</p>
                <div className="flex flex-wrap gap-2">
                  {ev.panel.map((p) => (
                    <span key={p} className="text-[11px] px-3 py-1 rounded-full bg-[#1a1a1a] border border-white/10 text-zinc-300">{p}</span>
                  ))}
                </div>
              </div>
              <div className="mt-6">
                <button
                  data-testid={`event-register-btn-${ev.id}`}
                  onClick={() => setSelected(ev)}
                  className="px-7 py-2.5 rounded-full bg-[#F5B301] text-black text-sm font-bold hover:bg-[#D99E01] transition-colors"
                >
                  Register Now
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#121212] border border-white/10 text-white sm:max-w-md" data-testid="register-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-left">Register for {selected?.title}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-left text-sm">
              {selected?.date} · {selected?.location}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={register} className="space-y-4 mt-2 text-left">
            <input
              data-testid="register-name-input"
              placeholder="Full name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-2.5 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#F5B301]/50"
            />
            <input
              data-testid="register-email-input"
              type="email"
              placeholder="Email address"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-2.5 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#F5B301]/50"
            />
            <div className="flex gap-3">
              {["investor", "founder"].map((r) => (
                <button
                  key={r}
                  type="button"
                  data-testid={`register-role-${r}`}
                  onClick={() => setForm({ ...form, role: r })}
                  className={`flex-1 py-2 rounded-lg text-xs font-semibold capitalize border transition-colors ${
                    form.role === r ? "bg-[#F5B301] text-black border-[#F5B301]" : "border-white/15 text-zinc-400"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
            <button
              data-testid="register-submit-btn"
              type="submit"
              disabled={submitting}
              className="w-full py-3 rounded-full bg-[#F5B301] text-black text-sm font-bold hover:bg-[#D99E01] transition-colors disabled:opacity-60"
            >
              {submitting ? "Registering…" : "Confirm Registration"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </main>
  );
}
